import { useState } from 'react';
import { api } from '../api';
import { applyClaimResult, useDailyStatus } from '../dailyStatusStore';
import { useT, type TKey } from '../i18n';
import type { QuestId } from '@cradle7/shared';

export function QuestList() {
  const status = useDailyStatus();
  const { t } = useT();
  const [claiming, setClaiming] = useState<QuestId | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!status) return null;

  async function handleClaim(questId: QuestId) {
    if (claiming) return;
    setClaiming(questId);
    setError(null);
    try {
      const result = await api.claimQuest(questId);
      applyClaimResult(result);
    } catch (e) {
      setError(String(e));
    } finally {
      setClaiming(null);
    }
  }

  const claimedCount = status.quests.filter((q) => q.claimed).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--fg-text-dim)' }}>
        <span>{t('quests.title')}</span>
        <span>
          {t('quests.claimedOfTotal')
            .replace('{claimed}', String(claimedCount))
            .replace('{total}', String(status.quests.length))}
        </span>
      </div>
      {status.quests.map((q) => {
        const pct = q.target > 0 ? (q.progress / q.target) * 100 : 0;
        const canClaim = q.completed && !q.claimed && claiming !== q.id;
        return (
          <div
            key={q.id}
            style={{
              padding: '6px 10px',
              background: q.claimed ? 'rgba(0,255,180,0.07)' : 'rgba(0,0,16,0.4)',
              border: `1px solid ${q.claimed ? 'rgba(0,255,180,0.4)' : 'var(--fg-muted)'}`,
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 12, color: q.claimed ? 'var(--fg-success)' : 'var(--fg-primary)' }}>
                  {t(q.nameKey as TKey)}
                </span>
                <span style={{ fontSize: 10, color: 'var(--fg-text-dim)' }}>
                  {t(q.descKey as TKey)}
                </span>
              </div>
              <span style={{ fontSize: 11, color: 'var(--fg-warning)' }}>+{q.rewardFishies} 🐟</span>
            </div>
            <div style={{ height: 4, background: 'rgba(0,0,16,0.6)', position: 'relative', border: '1px solid var(--fg-muted)' }}>
              <div
                style={{
                  width: `${Math.min(100, pct)}%`,
                  height: '100%',
                  background: q.claimed ? 'var(--fg-success)' : 'var(--fg-primary)',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--fg-text-dim)', fontVariantNumeric: 'tabular-nums' }}>
                {q.progress} / {q.target}
              </span>
              {q.claimed
                ? <span style={{ fontSize: 10, color: 'var(--fg-success)' }}>{t('quests.claimed')}</span>
                : (
                    <button
                      type="button"
                      disabled={!canClaim}
                      onClick={() => handleClaim(q.id)}
                      style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        background: canClaim ? 'var(--fg-warning)' : 'transparent',
                        color: canClaim ? '#001020' : 'var(--fg-muted)',
                        border: '1px solid currentColor',
                        cursor: canClaim ? 'pointer' : 'default',
                      }}
                    >
                      {claiming === q.id ? '…' : t('quests.claim')}
                    </button>
                  )}
            </div>
          </div>
        );
      })}
      {error && <div style={{ fontSize: 10, color: 'var(--fg-danger)' }}>{error}</div>}
    </div>
  );
}
